import { Button } from '@/components/ui/button';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { useGetFileService } from '@/services/tableViews';
import { Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

import { Loader2, X } from 'lucide-react';
import type { FC } from 'react';

type TableViewDocumentPreviewProps = {
  fileId: string;
  fileName: string;
  onClose: () => void;
};

const PdfViewerContent: FC<{ fileUrl: string }> = ({ fileUrl }) => {
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  return (
    <div className="flex-1 overflow-hidden">
      <Viewer
        fileUrl={fileUrl}
        plugins={[defaultLayoutPluginInstance]}
        defaultScale={1}
      />
    </div>
  );
};

export const TableViewDocumentPreview: FC<TableViewDocumentPreviewProps> = ({
  fileId,
  fileName,
  onClose,
}) => {
  const { fileUrl, isPending: isLoadingFile } = useGetFileService(fileId);

  return (
    <FlexCol
      className="fixed z-50 bg-white rounded-lg shadow-2xl border border-neutral-200 overflow-hidden"
      style={{
        top: '50%',
        left: 'calc(50% - 150px)',
        transform: 'translate(-50%, -50%)',
        width: 'min(900px, calc(100vw - 440px))',
        height: 'calc(100vh - 60px)',
      }}
    >
      {/* Header */}
      <FlexRow
        alignItems="center"
        justifyContent="between"
        className="px-4 py-3 border-b border-neutral-200 shrink-0"
      >
        <Text variant="body-sm" className="font-medium truncate max-w-[400px]">
          {fileName}
        </Text>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="h-8 w-8"
        >
          <X className="w-4 h-4" />
        </Button>
      </FlexRow>

      {/* Content */}
      {isLoadingFile ? (
        <FlexCol
          alignItems="center"
          justifyContent="center"
          className="flex-1"
          gap="3"
        >
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <Text variant="body-sm" color="tertiary">
            Loading document...
          </Text>
        </FlexCol>
      ) : fileUrl ? (
        <PdfViewerContent fileUrl={fileUrl} />
      ) : (
        <FlexCol alignItems="center" justifyContent="center" className="flex-1">
          <Text variant="body-sm" color="tertiary">
            Unable to load document
          </Text>
        </FlexCol>
      )}
    </FlexCol>
  );
};
